'use client';
import Image from 'next/image';
import Link from 'next/link';
import { useTranslations } from 'next-intl';
import type React from 'react';
import Carousel, { ResponsiveType } from 'react-multi-carousel';
import 'react-multi-carousel/lib/styles.css';

import { CinemaPartnerDto } from '@cineamo/api/models/cinema-partner/CinemaPartnerDto.types';

import { DomainConfigurationDto } from '@/types/DomainConfigurationDto';

import { DeviceType } from '@cineamo/api/constants/DeviceType.enum';

import { getExternalLinkTarget } from '@/helpers/external-link-helper';
import { useDeviceType } from '@cineamo/ui/src/helpers/screen-type';
import { tailwindBreakpoints } from '@cineamo/ui/src/helpers/tailwind-config-helper';

import CenterLayout from '@cineamo/composables/src/CenterLayout';

export const responsive: ResponsiveType = {
    [DeviceType.DESKTOP]: {
        breakpoint: { max: 3000, min: Number(tailwindBreakpoints.lg.replace('px', '')) },
        items: 5
    },
    [DeviceType.TABLET]: {
        breakpoint: {
            max: Number(tailwindBreakpoints.lg.replace('px', '')),
            min: Number(tailwindBreakpoints.md.replace('px', ''))
        },
        items: 3
    },
    [DeviceType.MOBILE]: {
        breakpoint: { max: Number(tailwindBreakpoints.md.replace('px', '')), min: 0 },
        items: 1
    }
};

const CinemaPartners: React.FC<{
    partners: CinemaPartnerDto[];
    domainConfiguration: DomainConfigurationDto;
}> = ({ partners, domainConfiguration }) => {
    const t = useTranslations();
    const deviceType = useDeviceType();
    const itemsPerScreen = responsive[deviceType]?.items ?? responsive.mobile.items;
    const isSliding = partners.length > itemsPerScreen;
    const externalLinkTarget = getExternalLinkTarget(domainConfiguration?.websiteConfiguration);

    if (!partners.length) {
        return null;
    }

    const renderPartner = (partner: CinemaPartnerDto) => {
        const partnerBlockContent = partner.logoImageUrl ? (
            <Image
                src={partner.logoImageUrl}
                alt={partner.name}
                width={1000}
                height={1000}
                className="w-full h-full object-contain pointer-events-none"
                style={{ objectFit: 'contain' }}
                // Asset URLs have no file extension, so the custom CDN loader can't tell SVGs
                // apart from raster images and always routes them through the optimizer/resize
                // pipeline. That pipeline can't rasterize vector sources, so real SVG logos
                // (unlike cinema logos, which are always rasterized client-side before upload)
                // fail to load. Skip the loader and fetch the asset as-is.
                unoptimized
            />
        ) : (
            <span>{partner.name}</span>
        );

        const wrapperClassName = 'p-24 w-full h-full rounded-16 flex items-center justify-center';
        let link = null;
        if (partner.link) {
            link = partner.link.startsWith('http') ? partner.link : `https://${partner.link}`;
        }

        const backgroundColor = partner.logoBackgroundColor ?? domainConfiguration.style.surfaceColor;

        return link ? (
            <Link
                href={link}
                key={partner.id}
                target={externalLinkTarget.target}
                rel={externalLinkTarget.rel}
                style={{ backgroundColor }}
                className={wrapperClassName}>
                {partnerBlockContent}
            </Link>
        ) : (
            <div
                key={partner.id}
                style={{ backgroundColor }}
                className={wrapperClassName}>
                {partnerBlockContent}
            </div>
        );
    };

    return (
        <CenterLayout>
            <div
                className="flex flex-col space-y-24 my-[48px] gradient-mask-r-[transparent,black_10%,black_90%,transparent_100%]"
                id="partners">
                <div className="text-center">
                    <h3 className="text-body-bold-16">{t('info.partners')}</h3>
                    <p className="text-body-14">{t('info.partners-description')}</p>
                </div>

                {isSliding ? (
                    <Carousel
                        key={deviceType}
                        className="w-full"
                        responsive={responsive}
                        arrows={false}
                        autoPlay
                        autoPlaySpeed={1500}
                        infinite
                        shouldResetAutoplay
                        centerMode={false}
                        ssr={true}
                        deviceType={deviceType}
                        partialVisible={false}
                        itemClass="flex-shrink-0 h-[130px] w-[200px] px-8">
                        {partners.map(renderPartner)}
                    </Carousel>
                ) : (
                    <div className="w-full flex flex-row flex-wrap justify-center">
                        {partners.map((partner) => (
                            <div
                                key={partner.id}
                                className="flex-shrink-0 h-[130px] w-[200px] px-8">
                                {renderPartner(partner)}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </CenterLayout>
    );
};

export default CinemaPartners;
